if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      mediaImageReadyPromises = new WeakMap();
      mediaSelectionSequence = 0;

      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.curatedCounterObserver = new ResizeObserver(() =>
          window.requestAnimationFrame(() => this.syncCuratedCounter())
        );
        this.curatedCounterObserver.observe(this.elements.viewer);
        window.requestAnimationFrame(() => this.syncCuratedCounter());
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.viewer.addEventListener('pointerdown', this.onViewerPointerDown.bind(this), true);
        this.elements.viewer.addEventListener('click', this.onViewerClick.bind(this), true);
        this.bindThumbnailListeners();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      disconnectedCallback() {
        this.curatedCounterObserver?.disconnect();
      }

      bindThumbnailListeners() {
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (!button || button.dataset.mediaGalleryBound === 'true') return;
          button.dataset.mediaGalleryBound = 'true';
          const mediaId = mediaToSwitch.dataset.target;
          const prepareImage = () => this.prepareMediaImage(mediaId);
          button.addEventListener('pointerenter', prepareImage, { passive: true });
          button.addEventListener('focus', prepareImage);
          button.addEventListener('touchstart', prepareImage, { passive: true });
          button.addEventListener('click', () => {
            this.pendingInteraction = null;
            this.trackGalleryInteraction('thumbnail', mediaId);
            this.setActiveMedia(mediaId, false);
          });
        });
      }

      prepareMediaImage(mediaId) {
        const image = this.elements.viewer
          .querySelector(`[data-media-id="${mediaId}"]`)
          ?.querySelector('img');
        if (!image) return Promise.resolve();
        image.loading = 'eager';
        image.fetchPriority = 'high';

        const existing = this.mediaImageReadyPromises.get(image);
        if (existing) return existing;

        const decoded = typeof image.decode === 'function'
          ? image.decode().catch(() => undefined)
          : new Promise((resolve) => {
              if (image.complete) {
                resolve();
                return;
              }
              image.addEventListener('load', resolve, { once: true });
              image.addEventListener('error', resolve, { once: true });
            });
        let timeoutId;
        const timeout = new Promise((resolve) => {
          timeoutId = window.setTimeout(resolve, 5000);
        });
        const ready = Promise.race([decoded, timeout]).finally(() => window.clearTimeout(timeoutId));
        this.mediaImageReadyPromises.set(image, ready);
        return ready;
      }

      onViewerPointerDown(event) {
        if (event.target.closest('.slider-button, .product__media-toggle')) return;
        this.pendingInteraction = { action: 'swipe', startedAt: Date.now() };
      }

      onViewerClick(event) {
        if (event.target.closest('.slider-button')) {
          this.pendingInteraction = { action: 'arrow', startedAt: Date.now() };
          return;
        }

        const mediaToggle = event.target.closest('.product__media-toggle');
        if (!mediaToggle) return;
        const mediaItem = mediaToggle.closest('.product__media-item[data-media-id]');
        this.trackGalleryInteraction('zoom', mediaItem?.dataset.mediaId);
      }

      onSlideChanged(event) {
        const currentElement = event.detail?.currentElement;
        if (!currentElement) return;
        this.syncCuratedCounter(currentElement);
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);

        const pendingInteraction = this.pendingInteraction;
        this.pendingInteraction = null;
        if (pendingInteraction && Date.now() - pendingInteraction.startedAt < 3000) {
          this.trackGalleryInteraction(pendingInteraction.action, currentElement.dataset.mediaId);
        }
      }

      trackGalleryInteraction(action, mediaId) {
        const mediaItems = [...this.elements.viewer.querySelectorAll('[data-curated-role]')];
        const mediaItem =
          this.elements.viewer.querySelector(`[data-curated-role][data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('.is-active') ||
          mediaItems[0];
        if (!mediaItem) return;

        const payload = {
          action,
          media_position: Math.max(1, mediaItems.indexOf(mediaItem) + 1),
          media_count: mediaItems.length,
          media_role: mediaItem.dataset.curatedRole || 'shared',
          media_id: mediaItem.dataset.mediaId?.split('-').pop() || '',
          product_id: this.dataset.productId || '',
          product_handle: this.dataset.productHandle || '',
          variant_id: this.dataset.variantId || '',
        };

        try {
          const published = window.Shopify?.analytics?.publish?.('aestara:product_gallery_interaction', payload);
          published?.catch?.(() => {});
        } catch (_error) {
          // Analytics must never interfere with the gallery experience.
        }

        try {
          window.clarity?.('event', `product_gallery_${action}`);
        } catch (_error) {
          // Clarity may be unavailable when consent or content blockers prevent it loading.
        }

        try {
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'product_gallery_interaction', payload);
          } else if (Array.isArray(window.dataLayer)) {
            window.dataLayer.push({ event: 'product_gallery_interaction', ...payload });
          }
        } catch (_error) {
          // Google Analytics may be unavailable when consent or content blockers prevent it loading.
        }
      }

      syncCuratedCounter(currentElement) {
        const items = [...this.elements.viewer.querySelectorAll('[data-curated-role]')];
        if (!items.length) return;
        this.elements.viewer.totalPages = items.length;
        const current = this.elements.viewer.querySelector('.slider-counter--current');
        const total = this.elements.viewer.querySelector('.slider-counter--total');
        const activeItem = currentElement || this.elements.viewer.querySelector('.is-active') || items[0];
        const activeIndex = Math.max(0, items.indexOf(activeItem));
        if (current) current.textContent = String(activeIndex + 1);
        if (total) total.textContent = String(items.length);
      }

      async setActiveMedia(mediaId, prepend) {
        const selectionSequence = ++this.mediaSelectionSequence;
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        await this.prepareMediaImage(activeMedia.dataset.mediaId);
        if (selectionSequence !== this.mediaSelectionSequence || !activeMedia.isConnected) return;

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail?.parentElement) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          const activeMediaParent = activeMedia.parentElement;
          if (!activeMedia.isConnected || !activeMediaParent) return;

          if (!this.mql.matches || this.elements.thumbnails) {
            this.setDirectMediaPosition(activeMediaParent, activeMedia.offsetLeft);
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const stickyHeader = document.querySelector('.section-header');
          const stickyHeaderRect = stickyHeader?.getBoundingClientRect();
          const stickyHeaderOffset = stickyHeaderRect ? Math.max(0, stickyHeaderRect.bottom) : 0;
          const top = activeMediaRect.top + window.scrollY - stickyHeaderOffset - 8;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        this.syncCuratedCounter(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setDirectMediaPosition(container, left) {
        const previousLeft = container.scrollLeft;
        try {
          container.scrollTo({ left, behavior: 'instant' });
        } catch (_error) {
          // Older browsers can reject the newer `instant` behavior value.
        }

        if (Math.abs(container.scrollLeft - previousLeft) > 1 || Math.abs(left - previousLeft) <= 1) return;

        const previousBehavior = container.style.getPropertyValue('scroll-behavior');
        const previousPriority = container.style.getPropertyPriority('scroll-behavior');
        container.style.setProperty('scroll-behavior', 'auto', 'important');
        void container.offsetWidth;
        container.scrollLeft = left;
        window.requestAnimationFrame(() => {
          if (previousBehavior) {
            container.style.setProperty('scroll-behavior', previousBehavior, previousPriority);
          } else {
            container.style.removeProperty('scroll-behavior');
          }
        });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        const thumbnailButton = thumbnail.querySelector('button');
        if (!thumbnailButton) return;
        thumbnailButton.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider?.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
